import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LogoPumpp from './Logopng.png';
import HubspotForm from './HubspotForm';


// Color palette
const colors = {
  deepGraphite: '#121212',
  trueBlack: '#000000',
  vascularRed: '#FF2A2A',
  neonOrange: '#FF7B00',
  electricBlue: '#00E5FF',
  white: '#FFFFFF',
  secondaryText: '#A3A3A3'
};

// Typography
const typography = {
  heading: { fontFamily: "'Inter', sans-serif", fontWeight: 800, fontStyle: 'italic' },
  body: { fontFamily: "'Inter', sans-serif", fontWeight: 400 },
  numbers: { fontFamily: "'Space Mono', monospace" }
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const pulse = {
  scale: [1, 1.02, 1],
  transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
};

const LandingPage = () => {
  const [formData, setFormData] = useState({ nome: '', email: '', telefone: '', atividade: '' });
  const formRef = useRef(null);

  // Scroll to form when CTA button clicked
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  

  
 // Handle form submission - aqui ficava o código
  


  // Feature cards data
  const features = [
    { icon: '🔥', title: 'Pumpp Streak', description: 'Mantenha sua sequência de treinos e nunca perca o ritmo.' },
    { icon: '⚡', title: 'The Daily Pumpp', description: 'Treinos diários projetados para maximizar seus ganhos.' },
    { icon: '💪', title: 'Solte o Pumpp', description: 'Libere todo o seu potencial com exercícios explosivos.' }
  ];

  // Hook for scroll-triggered animations
  const [refHero, inViewHero] = useInView({ triggerOnce: true, threshold: 0.3 });
  const [refFeatures, inViewFeatures] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [refFinal, inViewFinal] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <div style={{ backgroundColor: colors.trueBlack, color: colors.white, minHeight: '100vh', fontFamily: typography.body.fontFamily }}>
      {/* ========== HEADER ========== */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 40px', backgroundColor: colors.deepGraphite, borderBottom: `1px solid ${colors.secondaryText}33` }}>
        <img src={LogoPumpp} alt="Pumpp Logo" style={{ height: '80px' }} />
        <button onClick={scrollToForm} style={{ padding: '10px 20px', backgroundColor: colors.vascularRed, color: colors.white, border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', ...typography.heading }}>
          Entrar na Tribo
        </button>
      </header>

      {/* ========== HERO SECTION ========== */}
      <motion.section
        ref={refHero}
        initial={{ opacity: 0 }}
        animate={inViewHero ? { opacity: 1, transition: { duration: 1 } } : {}}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '90vh', textAlign: 'center', padding: '40px 20px' }}
      >
        <motion.img
          src={LogoPumpp}
          alt="Pumpp Logo"
          style={{ height: '120px', marginBottom: '30px' }}
          initial={{ scale: 0 }}
          animate={{ scale: 1, transition: { type: 'spring', stiffness: 120, delay: 0.3 } }}
        />
        <motion.h1 style={{ ...typography.heading, fontSize: '3rem', marginBottom: '20px', lineHeight: 1.2 }}>
          Bem-vindo ao Pumpp
        </motion.h1>
        <motion.p style={{ ...typography.body, fontSize: '1.2rem', color: colors.secondaryText, maxWidth: '600px', marginBottom: '40px' }}>
          Junte-se à tribo que nunca falha um dia.
        </motion.p>
        <motion.button
          onClick={scrollToForm}
          animate={pulse}
          whileHover={{ scale: 1.05, backgroundColor: colors.neonOrange }}
          style={{ padding: '18px 40px', backgroundColor: colors.vascularRed, color: colors.white, border: 'none', borderRadius: '50px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 0 20px rgba(255,42,42,0.5)', ...typography.heading }}
        >
          Quero Participar do Beta
        </motion.button>
      </motion.section>

      {/* ========== FEATURES SECTION ========== */}
      <section style={{ padding: '80px 20px', backgroundColor: colors.deepGraphite }}>
        <motion.div
          ref={refFeatures}
          initial="hidden"
          animate={inViewFeatures ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto' }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={{ scale: 1.05, boxShadow: `0 10px 30px ${colors.neonOrange}33` }}
              style={{ backgroundColor: colors.trueBlack, padding: '40px 20px', borderRadius: '16px', textAlign: 'center', border: `1px solid ${colors.secondaryText}22` }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>{feature.icon}</div>
              <h3 style={{ ...typography.heading, fontSize: '1.8rem', marginBottom: '10px' }}>{feature.title}</h3>
              <p style={{ ...typography.body, color: colors.secondaryText, fontSize: '1rem' }}>{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ========== FINAL CTA / MANIFESTO ========== */}
      <motion.section
        ref={refFinal}
        initial="hidden"
        animate={inViewFinal ? 'visible' : 'hidden'}
        variants={fadeInUp}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center', backgroundColor: colors.trueBlack }}
      >
        <h2 style={{ ...typography.heading, fontSize: '2.5rem', maxWidth: '900px', marginBottom: '50px', lineHeight: 1.3 }}>
          "Em 2026, você ainda vai se desculpar por faltar? Ou vai fazer parte da Tribo que nunca falha um dia?"
        </h2>
        <div ref={formRef} style={{ width: '100%', maxWidth: '500px' }}>
        <HubspotForm />
        </div>
      </motion.section>

      {/* ========== FOOTER ========== */}
      <footer style={{ backgroundColor: colors.deepGraphite, padding: '30px 20px', textAlign: 'center', borderTop: `1px solid ${colors.secondaryText}33` }}>
        <p style={{ ...typography.body, color: colors.secondaryText, fontSize: '0.9rem', margin: 0 }}>
          © 2026 Pumpp Inc. Feito para quem não falha um dia.
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;